import { isStandardPalletType } from '../../constants/pallets';
import * as Yup from 'yup';
import { ProductSkusFormValues } from './types';

const optionalNumberSchema = Yup.string()
  .optional()
  .nullable()
  .test(
    'min-if-present',
    'Must be at least 1',
    (value) => !value || Number(value) >= 1
  )
  .matches(/^\d*\.?\d*$/, 'Must be a valid number');

const positiveNumberSchema = Yup.string()
  .optional()
  .nullable()
  .test(
    'positive-if-present',
    'Must be greater than 0',
    (value) => !value || Number(value) > 0
  )
  .matches(/^\d*\.?\d*$/, 'Must be a valid number');

function isOneWhenSkuUnitIsPallet(
  this: Yup.TestContext,
  value?: string | null
) {
  const skuEntry = this.from?.[1]?.value as
    | { unitOfMeasure?: { value?: string } }
    | undefined;
  if (skuEntry?.unitOfMeasure?.value !== 'Pallet') return true;
  return Number(value) === 1;
}

const palletSchema = Yup.object({
  skuId: Yup.string(),
  type: Yup.string().required('Pallet type is required'),
  customPalletSpecification: Yup.string().when('type', {
    is: (type: string) => !isStandardPalletType(type),
    then: (_schema) =>
      _schema.required('A specification is required for this pallet type'),
    otherwise: (_schema) => _schema.optional(),
  }),
  amountOfSkusInLayer: optionalNumberSchema
    .required('Required')
    .test(
      'one-per-layer-for-pallet-sku',
      'A Pallet SKU is fixed at 1',
      isOneWhenSkuUnitIsPallet
    ),
  amountOfLayersInPallet: optionalNumberSchema
    .required('Required')
    .test(
      'one-layer-for-pallet-sku',
      'A Pallet SKU is fixed at 1',
      isOneWhenSkuUnitIsPallet
    ),
  customPalletWeightUnit: Yup.object().shape({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }),
  customPalletWeight: positiveNumberSchema.when('type', (type, _schema) => {
    const value = Array.isArray(type) ? type[0] : type;
    return value === 'Custom Pallet'
      ? _schema.required('A pallet weight is required for this pallet type')
      : _schema.optional().nullable();
  }),
  length: Yup.string()
    .optional()
    .nullable()
    .test(
      'positive-if-present',
      'Must be greater than 0',
      (value) => !value || Number(value) > 0
    )
    .matches(/^\d*\.?\d*$/, 'Must be a valid number')
    .when('type', (type, _schema) => {
      const value = Array.isArray(type) ? type[0] : type;
      return !isStandardPalletType(value)
        ? _schema.required('Length is required for this pallet type')
        : _schema.optional().nullable();
    }),
  width: Yup.string()
    .optional()
    .nullable()
    .test(
      'positive-if-present',
      'Must be greater than 0',
      (value) => !value || Number(value) > 0
    )
    .matches(/^\d*\.?\d*$/, 'Must be a valid number')
    .when('type', (type, _schema) => {
      const value = Array.isArray(type) ? type[0] : type;
      return !isStandardPalletType(value)
        ? _schema.required('Width is required for this pallet type')
        : _schema.optional().nullable();
    }),
  height: Yup.string()
    .optional()
    .nullable()
    .test(
      'positive-if-present',
      'Must be greater than 0',
      (value) => !value || Number(value) > 0
    )
    .matches(/^\d*\.?\d*$/, 'Must be a valid number')
    .when('type', (type, _schema) => {
      const value = Array.isArray(type) ? type[0] : type;
      return !isStandardPalletType(value)
        ? _schema.required('Height is required for this pallet type')
        : _schema.optional().nullable();
    }),
  dimensionsUnitOfMeasure: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.string(),
    })
    .when('type', (type, _schema) => {
      const value = Array.isArray(type) ? type[0] : type;
      return !isStandardPalletType(value)
        ? _schema.required('Unit is required for this pallet type')
        : _schema.optional().nullable();
    }),
});

const skuEntrySchema = Yup.object().shape({
  skuId: Yup.string()
    .required('SKU ID is required')
    .test(
      'unique-sku-id',
      'This SKU ID is used more than once',
      function isUniqueWithinForm(value) {
        if (!value) return true;
        const root = this.from?.[this.from.length - 1]?.value as
          | ProductSkusFormValues
          | undefined;
        const cards = root?.skus ?? [];
        const matches = cards.filter(
          (card) => (card?.skuId ?? '').trim() === value.trim()
        );
        return matches.length <= 1;
      }
    ),
  name: Yup.string().required('SKU Name is required'),
  barcode: Yup.string().optional().nullable(),
  unitOfMeasure: Yup.object().shape({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }),
  amountOfProductInSku: Yup.string()
    .required('Products per SKU is required')
    .matches(/^\d*\.?\d*$/, 'Must be a valid number')
    .test('min-1', 'Must be at least 1', (value) => Number(value) >= 1)
    .when('unitOfMeasure', {
      is: (unitOfMeasure: { value?: string } | undefined) =>
        unitOfMeasure?.value === 'Item',
      then: (s) =>
        s.test(
          'item-must-be-1',
          'Must be 1 when the unit of measure is Item',
          (value) => Number(value) === 1
        ),
      otherwise: (s) => s,
    }),
  lowStockThreshold: Yup.string()
    .optional()
    .nullable()
    .test(
      'min-0-if-present',
      'Must be 0 or more',
      (value) => !value || Number(value) >= 0
    )
    .matches(/^\d*\.?\d*$/, 'Must be a valid number'),
  description: Yup.string().optional().nullable(),
  dimensions: Yup.object().shape({
    dimensionsUnitOfMeasure: Yup.object().shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    }),
    length: positiveNumberSchema.required('Required'),
    width: positiveNumberSchema.required('Required'),
    height: positiveNumberSchema.required('Required'),
  }),
  grossWeightUnitOfMeasure: Yup.object().shape({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }),
  grossWeight: positiveNumberSchema.required('Required'),
  palletising: Yup.array().of(palletSchema),
});

export const productSkusSchema = Yup.object().shape({
  productId: Yup.string().required('Product ID is required'),
  productName: Yup.string().optional().nullable(),
  productDescription: Yup.string().optional().nullable(),
  productBrand: Yup.string().optional().nullable(),
  productCategory: Yup.string().optional().nullable(),
  productSubCategory: Yup.string().optional().nullable(),
  productNetVolume: positiveNumberSchema.optional().nullable(),
  productNetVolumeUnitOfMeasure: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.string(),
    })
    .optional()
    .nullable(),
  productBarcode: Yup.string().optional().nullable(),
  skus: Yup.array().min(1, 'Add at least one SKU').of(skuEntrySchema),
});
